import { Trans, useTranslation } from 'react-i18next';
import { Breakpoint, Typography } from 'app/component-library-wave';
import { Link, QuickLink } from '@lyse-as/formds-react';
import { ChevronRightIcon } from '@lyse-as/formds-react/lib/icon/common';

import { ContainerFixed } from 'app/components/container';
import { CustomerPage } from 'app/pages/page-wrapper';
import { navigationService } from 'app/service/navigation/navigation-service';
import { useAppNavigation } from 'app/utils/navigation-utils';
import { Partner } from 'app/utils/partner-utils';

import styles from './privacy-and-concents.module.scss';
import { Page } from 'app/service/navigation/pages/page-types';
import { getDataLayers } from 'app/utils/trackingService';
import {
  anonymizeUrl,
  getAnonymizedCurrentPageUrl,
  getGtmFullUrl,
  GtmElementCategory,
  GtmElementCta,
} from 'app/utils/gtm-tracking-utils';

export const LYSE_GENERAL_MARKETING_CONSENT_URL =
  'https://www.lyse.no/minesider/mitt-kundeforhold/samtykke#markedsforing';
const LYSE_RESERVATIONS_URL = 'https://www.lyse.no/minesider/mitt-kundeforhold/samtykke#reservasjoner';

export const privacyAndConsents: CustomerPage = (props) => {
  const { t } = useTranslation();
  const { goToPath } = useAppNavigation();

  const getNavItemHref = (page: Page): string => {
    if (props.userContext.partnerNumber !== Partner.NO_Lyse) {
      return page.url;
    }

    switch (page) {
      case navigationService.PAGES.generalMarketing:
        return LYSE_GENERAL_MARKETING_CONSENT_URL;
      case navigationService.PAGES.reservations:
        return LYSE_RESERVATIONS_URL;
      default:
        return page.url;
    }
  };

  const handleListItemClick = (page: Page, elementCta: GtmElementCta) => {
    getDataLayers().customEvents.pushButtonClickEvent({
      element_category: GtmElementCategory.PRIVACY_AND_CONCENTS,
      element_cta: elementCta,
      page_url: getGtmFullUrl(navigationService.PAGES.privacyAndConsents.url),
      element_url: getGtmFullUrl(getNavItemHref(page)),
    });
  };

  return (
    <ContainerFixed isNarrow={true}>
      <div className={styles.privacyAndConcentContainer}>
        <Typography variant="headline4" component="h1" className={styles.header} maxBreakpoint={Breakpoint.TABLET}>
          {t('pages.consent.privacyAndConsents.heading')}
        </Typography>

        <Typography variant="paragraph2" component="p" className={styles.lead} maxBreakpoint={Breakpoint.MOBILE}>
          {t('pages.consent.privacyAndConsents.lead')}
        </Typography>

        <div className={styles.navList} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <QuickLink
            title={t('pages.consent.privacyAndConsents.navlist.generalMarketing')}
            description={t('pages.consent.privacyAndConsents.navlist.generalMarketingSecondary')}
            href={getNavItemHref(navigationService.PAGES.generalMarketing)}
            rightIcon={<ChevronRightIcon />}
            onClick={(e) => {
              e.preventDefault();
              handleListItemClick(
                navigationService.PAGES.generalMarketing,
                GtmElementCta.PRIVACY_AND_CONCENTS_GENERAL_MARKETING,
              );
              goToPath(getNavItemHref(navigationService.PAGES.generalMarketing));
            }}
          />
          <QuickLink
            title={t('pages.consent.privacyAndConsents.navlist.reservations')}
            description={t('pages.consent.privacyAndConsents.navlist.reservationsSecondary')}
            href={getNavItemHref(navigationService.PAGES.reservations)}
            rightIcon={<ChevronRightIcon />}
            onClick={(e) => {
              e.preventDefault();
              handleListItemClick(
                navigationService.PAGES.reservations,
                GtmElementCta.PRIVACY_AND_CONCENTS_RESERVATIONS,
              );
              goToPath(getNavItemHref(navigationService.PAGES.reservations));
            }}
          />

          <QuickLink
            title={t('pages.consent.privacyAndConsents.navlist.newsAndOffers')}
            description={t('pages.consent.privacyAndConsents.navlist.newsAndOffersSecondary')}
            href={navigationService.PAGES.newsAndOffers.url}
            rightIcon={<ChevronRightIcon />}
            onClick={(e) => {
              e.preventDefault();
              handleListItemClick(
                navigationService.PAGES.newsAndOffers,
                GtmElementCta.PRIVACY_AND_CONCENTS_NEWS_AND_OFFERS,
              );
              goToPath(navigationService.PAGES.newsAndOffers.url);
            }}
          />
        </div>

        <Typography variant="paragraph3" maxBreakpoint={Breakpoint.MOBILE} component="p" className={styles.moreInfo}>
          <Trans i18nKey="pages.consent.privacyAndConsents.privacyStatementLinkText">
            Les mer om{' '}
            <Link href="https://www.altibox.no/privat/kundeservice/personvern/personvernerklaering/">
              Altibox sin personvernserklæring
            </Link>
          </Trans>
        </Typography>
      </div>
    </ContainerFixed>
  );
};
